"use client";

/**
 * Блок «Новости» на главной — горизонтальная карусель с боковыми круглыми
 * стрелками (как у остальных каруселей сайта). Карточки одинаковой высоты
 * (items-stretch + h-full). Данные — из настроек (rent_site_global.data.news).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import CarouselArrow from "./CarouselArrow";
import NewsCard from "./NewsCard";
import type { NewsItem } from "@/lib/fleet-server";
import { useLocale } from "@/lib/locale-context";

export default function News({ items = [] }: { items?: NewsItem[] }) {
  const { t } = useLocale();
  const ref = useRef<HTMLDivElement | null>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  useEffect(() => {
    update();
    const el = ref.current;
    el?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { el?.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [update, items.length]);

  if (!items.length) return null;

  const scrollBy = (d: number) => ref.current?.scrollBy({ left: d, behavior: "smooth" });

  return (
    <div className="relative">
      <div
        ref={ref}
        className="flex snap-x items-stretch gap-6 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((n) => (
          <div key={n.slug} className="w-[300px] shrink-0 snap-start sm:w-[360px]">
            <NewsCard item={n} />
          </div>
        ))}
      </div>

      {canLeft && (
        <CarouselArrow
          direction="left"
          ariaLabel={t("a11y.prev")}
          onClick={() => scrollBy(-380)}
          className="!rounded-full absolute left-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]"
        />
      )}
      {canRight && (
        <CarouselArrow
          direction="right"
          ariaLabel={t("a11y.next")}
          onClick={() => scrollBy(380)}
          className="!rounded-full absolute right-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]"
        />
      )}
    </div>
  );
}
